import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { pinned } from '../../../../ui/components/pure/list-table/list-table-column-styles';
import { BadgeUserStatus } from '../../../../ui/components/pure/user-status-badge/badge-user-status';
import { CarrierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { CarrierSortFields } from '../logic/carriers-list-query.map';
import { MAX_CAPABILITY_ICONS, makeCarrierCapabilityIcons } from '../logic/carrier-capabilities.map';
import { NEEDS_REVIEW_LABEL, NEEDS_REVIEW_TOOLTIP, needsReview } from '../../logic/carrier-review';

// NAME is left unpinned and takes what is left.
const FIXED_COLUMN_WIDTHS = {
  sort: '80px',
  logo: '72px',
  capabilities: '220px',
  registrationPolicy: '180px',
  registered: '150px',
  status: '180px'
} as const;

const CAPABILITY_ICON_SIZE = '24px';

/** A group is `active` or `deleted`; the user badge renders both, it is just typed for users. */
const badgedStatus = (row: CarrierDto) => row.status as BadgeUserStatus;

const reviewLabel = (row: CarrierDto) => (needsReview(row) ? NEEDS_REVIEW_LABEL : '');

export function useCarriersTable() {
  const tb = initTable<CarrierDto, CarrierSortFields>();

  // Without this, scrolling a long name into view resizes every column.
  tb.styles({
    table: { tableLayout: 'fixed' }
  });

  tb.rowStyles({
    td: { height: '64px' }
  });

  // Where the carrier sits in a kiosk's list, lowest at the top. Blank where unset; the endpoint
  // coalesces those to the kiosk default before ordering, so they sort last rather than first.
  tb.column('SORT', TableComponents.Text, {
    value: (row) => (row.searchPriority === undefined ? '' : String(row.searchPriority))
  }).options({
    isSortable: true,
    sortId: 'searchPriority',
    minBreakpoint: 'md',
    tooltip: `the order this carrier is offered in`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.sort)
  });

  // `TenantLogo` falls back to the name when there is no image, so the name is empty on purpose —
  // the next column carries it, and a logoless carrier should leave a blank rather than repeat it.
  tb.column('', TableComponents.TenantLogo, {
    imageURL: ':image',
    name: `~`
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.logo)
  });

  // Unpinned, so it absorbs the leftover width; `maxWidth` ellipsises instead of overflowing. The
  // review badge sits under the name so a rare flag takes no column width.
  tb.columnGroup('NAME')
    .row(TableComponents.BoldText, {
      value: ':name',
      maxWidth: `~100%`
    })
    .row(TableComponents.NeedsReview, { label: reviewLabel, tooltip: `~${NEEDS_REVIEW_TOOLTIP}` })
    .options({
      isSortable: true,
      sortId: 'name',
      minBreakpoint: 'md',
      tooltip: `carrier name`
    });

  // Deliver and collect for one delivery type share an icon; the tooltip keeps both.
  tb.column('CAPABILITIES', TableComponents.MultipleSVGCell, {
    SVGs: (row) => makeCarrierCapabilityIcons(row).SVGs,
    tooltips: (row) => makeCarrierCapabilityIcons(row).tooltips,
    iconSize: `~${CAPABILITY_ICON_SIZE}`,
    maxVisibleSVGs: () => MAX_CAPABILITY_ICONS,
    align: `~left`
  }).options({
    // Not a sortBy the endpoint accepts.
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `what this carrier may do here`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.capabilities)
  });

  tb.column('REGISTRATION POLICY', TableComponents.RegistrationPolicy, {
    policy: ':kioskRegistrationPolicy'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `whether couriers may sign themselves up at a kiosk`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.registrationPolicy)
  });

  // `createdTime` is group creation — the DTO has no separate registration timestamp.
  tb.column('REGISTERED', TableComponents.DateOnly, {
    value: ':createdTime'
  }).options({
    isSortable: true,
    sortId: 'createdTime',
    minBreakpoint: 'md',
    tooltip: `when the carrier was created`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.registered)
  });

  tb.column('STATUS', TableComponents.UserStatus, {
    status: badgedStatus
  }).options({
    isSortable: true,
    sortId: 'status',
    minBreakpoint: 'md',
    tooltip: `carrier status`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.status)
  });

  // --- Mobile Columns ---
  tb.columnGroup('NAME')
    .row(TableComponents.BoldText, { value: ':name' })
    .row(TableComponents.RegistrationPolicy, { policy: ':kioskRegistrationPolicy' })
    .row(TableComponents.NeedsReview, { label: reviewLabel, tooltip: `~${NEEDS_REVIEW_TOOLTIP}` })
    .options({
      isSortable: true,
      sortId: 'name',
      maxBreakpoint: 'sm',
      tooltip: 'carrier name'
    });

  // The badge sizes itself on mobile, so a width here would fight it.
  tb.columnGroup('')
    .row(TableComponents.UserStatus, { status: badgedStatus })
    .row(TableComponents.DateOnly, { value: ':createdTime' })
    .options({
      isSortable: false,
      maxBreakpoint: 'sm',
      styles: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }
    });

  return tb;
}
